/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* global document */

const JSG = require('../../JSG');
const Point = require('../../geometry/Point');
const BooleanExpression = require('../expr/BooleanExpression');
const NumberExpression = require('../expr/NumberExpression');
const StringExpression = require('../expr/StringExpression');
const Expression = require('../expr/Expression');
const CellRange = require('./CellRange');
const TreeItem = require('./TreeItem');
const {setPropertiesDefinitions} = require('./utils');
const SheetModel = require('./SheetModel');

module.exports = class StreamSheetModel {
	constructor() {
		this._messages = [];

		this._sheet = new SheetModel();

		this._properties = {
			sheetId: '',
			name: '',
			isVisible: true,
			hideMessages: false,
			showInbox: true,
			// rows: 100,
			// columns: 50,
			maxChars: 1000,
			sheetType: 'sheet',
			stats : {
				steps : 0,
			},
			inbox: {
				split: 5000,
				width: 5000,
			},
			loop: {
				path: '',
				currentPath: '',
				enabled: false,
				recursively: false,
			},
			trigger: {
				type: 'interval',
				repeat: 'once',
				useDefaultInterval: true
			},
			stream: 'None',
			messageCount: 0,
			currentMessage: {
				id: -1,
				isProcessed: false
			}
		};
	}

	fromServer(serverSheet, initial) {
		const { id, loop, name, stats, sheet, trigger, settings, custom } = serverSheet;
		const streamSheetSettings = { name, loop, sheet: sheet.settings, trigger, settings };

		this._messages = [];
		this._sheet = new SheetModel(id, name);

		this._properties = {
			sheetId: id,
			name,
			isVisible: custom.jsg && custom.jsg.visible != null ? custom.jsg.visible : true,
			hideMessages: false,
			showInbox: true,
			// rows: 100,
			// columns: 50,
			maxChars: 1000,
			sheetType: custom.jsg && custom.jsg.type ? custom.jsg.type : 'sheet',
			stats,
			inbox: {
				split: custom.jsg && custom.jsg.inboxcontainer ? custom.jsg.inboxcontainer.split : 5000,
				width: custom.jsg && custom.jsg.inboxcontainer ? custom.jsg.inboxcontainer.width : 5000
			},
			loop,
			trigger,
			stream: 'None',
			currentMessage: {
				id: 0,
				isProcessed: false
			},
			messageCount: 0
		};

		if (this.properties.sheetType === 'dashboard') {
			if (initial) {
				const layoutNode = new JSG.LayoutNode();
				layoutNode.initNew();
				layoutNode.setId(layoutNode.newUniqueId());
				layoutNode.setSize(20000, 10000);
				layoutNode.getPin().setLocalPoint(0, 0);
				layoutNode.setOrigin(0, 0);
				layoutNode.getItemAttributes().setMoveable(JSG.ItemAttributes.Moveable.NONE);
				layoutNode.getItemAttributes().setDeleteable(false);

				this._sheet.shapes.shapes.push(layoutNode.toJSON(this._sheet));

				JSG.GraphUtils.traverseItem(layoutNode, item => {
					item.setId(item.newUniqueId());
					this._sheet.shapes.shapes.push(item.toJSON(this._sheet));
				}, false);

			}
			streamSheetSettings.settings.showGrid = false;
			streamSheetSettings.settings.showHeader = false;
			this._sheet.selection.clear();
		}

		this.updateStreamSheetSettings(streamSheetSettings);
	}

	get id() {
		return this._properties.sheetId;
	}

	get name() {
		return this._properties.name;
	}

	set name(name) {
		this._properties.name = name;
		this._sheet.name = name;
	}

	get isVisible() {
		return this._properties.isVisible;
	}

	set isVisible(doIt) {
		this._properties.isVisible = doIt;
	}

	get sheet() {
		return this._sheet;
	}

	get cells() {
		return this._sheet.cells;
	}

	get rows() {
		return this._sheet.rows;
	}

	get columns() {
		return this._sheet.columns;
	}

	get shapes() {
		return this._sheet.shapes;
	}

	get messages() {
		return this._messages;
	}

	get properties() {
		return this._properties;
	}

	createExpression({formula, type, value }) {
		switch (type) {
		case 'bool':
			return new BooleanExpression(value, formula);
		case 'number':
			return new NumberExpression(value, formula);
		case 'string':
			return new StringExpression(value, formula);
		default:
			return new Expression(value, formula);
		}
	}

	clearInbox() {
		this._messages.length = 0;
	}

	updateInbox(inbox = {}) {
		this.clearInbox();

		const { currentMessage, messages, stream, totalSize } = inbox;
		if (stream) {
			this.properties.stream = stream && stream.name ? stream.name : 'None';
		}
		if (currentMessage) {
			this.properties.currentMessage = currentMessage;
		}
		this.properties.messageCount = totalSize === undefined ? 0 : totalSize;

		// work on existing array
		messages.forEach((message) => {
			this._messages.push(TreeItem.fromMessage(message, message.metadata || message.Metadata, 'inboxcontainer'));
		});
	}

	updateSheet(cells, shapes, names, properties) {

		this.cells.markUpdate();

		cells.forEach((cellData) => {
			const res = CellRange.refToRC(cellData.reference, this);
			if (res === undefined) {
				return;
			}
			const pos = new Point(res.column - this._sheet._colOffset, res.row);

			const cell = this.cells.create(pos);
			if (
				cellData.value === undefined &&
				cellData.formula === undefined
			) {
				cell.clearContent();
			} else {
				let expr = cell.getExpression();
				let create;
				if (expr) {
					create = expr.getFormula() !== cellData.formula;
					if (!create) {
						switch (cellData.type) {
						case 'bool':
							create = !(expr instanceof BooleanExpression);
							break;
						case 'number':
							create = !(expr instanceof NumberExpression);
							break;
						case 'string':
							create = !(expr instanceof StringExpression);
							break;
						default:
							create = !(expr instanceof Expression);
							break;
						}
					}
				} else {
					create = true;
				}

				if (create) {
					expr = this.createExpression(cellData);
					cell.setExpression(expr);
					cell.evaluate(this._sheet);
				}

				// this is to ensure that temporary values form ui interaction (e.g. slider) are not overwritten
				const value = cellData.value;
				if (cell._targetValue !== undefined && cellData.value !== cell._targetValue) {
					cellData.value = cell._targetValue;
				}

				cell.setValue(cellData.value);
				cell.getExpression().setValue(value);
				// cell.evaluate(this._graphItem);

				if (cell._targetValue !== undefined && value === cell._targetValue) {
					cell._targetValue = undefined;
				}
			}
			cell.setInfo(cellData.info);

			// mark cell as updated, not updated cells will be cleared
			cell._updated = true;
		});

		this.cells.clearNotUpdated();

		if (names) {
			Object.keys(names).forEach((key) => {
				const name = this.cells.getOrCreateName(key);
				if (name) {
					const serverName = names[key];
					const expr = new Expression(
						serverName.value,
						serverName.formula
					);
					name.setExpression(expr);
					name.setValue(serverName.value);
					// name.evaluate(this._graphItem);
				}
			});
		}

		const { properties: sharedprops } = properties;
		setPropertiesDefinitions(this.rows, this.columns, this.cells, properties, sharedprops);


		shapes.shapes.forEach(shape => {
			if (shape.name && shape.name.v === undefined) {
				shape.name = {
					v: shape.name,
					t: 's'
				}
			} else if (shape.name === undefined) {
				shape.name = {
					v: shape.itemType ?
						`${shape.itemType}${shape.id}` :
						`${shape.shape.type}${shape.id}`,
					t: 's'
				}
			}
		});
		this._sheet.shapes = shapes;
	}

	updateStats(stats) {
		this.properties.stats = stats;
	}

	updateStream(stream) {
		if (stream !== null) {
			this.properties.stream = stream;
		}
	};

	updateStreamSheetSettings(sheetSettings = {}) {
		const { inbox, loop, name, settings, sheet, trigger } = sheetSettings;

		if (inbox) {
			this.updateStream(inbox.stream);
		}

		if (name) {
			this.name = name;
		}

		if (loop) {
			this.properties.loop = { ...loop};
		}

		if (sheet) {
			if (sheet.mincol != null) {
				this.columns.mincol = sheet.mincol;
			}
			if (sheet.maxcol != null) {
				this.columns.maxcol = sheet.maxcol;
			}
			if (sheet.maxrow != null) {
				this.rows.size = sheet.maxrow;
			}
			if (sheet.maxchars != null) {
				this.properties.maxChars = sheet.maxchars;
			}

			this.sheet.protected = !!sheet.protected;
		}

		if (trigger) {
			this.properties.trigger = { ...trigger};
		}

		if (settings) {
			const { greyIfRows, hideMessages, showFormulas, showGrid, showHeader, showInbox } = settings;
			if (showFormulas !== null && showFormulas !== undefined) {
				this.sheet.showFormulas = !!showFormulas;
			}
			if (showGrid !== null && showGrid !== undefined) {
				this.sheet.showGrid = !!showGrid;
			}
			if (showHeader !== null && showHeader !== undefined) {
				this.sheet.showHeader = !!showHeader;
			}
			if (greyIfRows !== null && greyIfRows !== undefined) {
				this.sheet.greyIfRows = !!greyIfRows;
			}
			if (showInbox !== null && showInbox !== undefined) {
				this.properties.showInbox = showInbox;
			}
			if (hideMessages !== null && hideMessages !== undefined) {
				this.properties.hideMessages = hideMessages;
			}
		}
	}
}
